import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { montant } from './format'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, Btn, Input, Label, Field, Row,
  Cluster, Muted, BackLink, Loading, Empty, Spinner, useUI,
} from './ui'
import { calcLigne, totauxDe, CAPACITE_CAMION, TAUX_TVA, tvaDe, tvaTotaleDe } from './carnetUtils'
import { ecrire } from './erreurs'

const Intro = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-left: 3px solid ${t.gold};
  border-radius: ${t.r};
  padding: 16px 18px;
  margin-bottom: 14px;
`
const IntroTitle = styled.h1`
  font-family: ${t.serif}; font-size: 1.24rem; font-weight: 660;
  letter-spacing: -0.034em; color: ${t.text}; margin: 0 0 5px;
`
const LigneBox = styled.div`
  background: ${t.surfaceAlt};
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  padding: 13px 15px;
  margin-bottom: 9px;
`
const LigneName = styled.div`
  font-family: ${t.font}; font-weight: 620; font-size: 0.88rem;
  letter-spacing: -0.016em; color: ${t.text}; margin-bottom: 10px;
`
/* Rappel de ce qui était prévu, sous chaque champ */
const Prevu = styled.div`
  font-family: ${t.font}; font-size: 0.66rem; color: ${t.textFaint};
  margin-top: 4px; font-variant-numeric: tabular-nums;
  b { color: ${(p) => (p.$diff ? t.amber : t.textFaint)}; font-weight: ${(p) => (p.$diff ? 640 : 500)}; }
`
const SousTotal = styled.div`
  display: flex; justify-content: flex-end; align-items: baseline; gap: 7px;
  margin-top: 10px; padding-top: 9px;
  border-top: 1px dashed ${t.line};
  font-family: ${t.font}; font-size: 0.73rem; color: ${t.textFaint};
  b { font-size: 0.88rem; font-weight: 640; color: ${t.green}; font-variant-numeric: tabular-nums; }
`
const Recap = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px;
  background: ${t.line}; border: 1px solid ${t.line};
  border-radius: ${t.rSm}; overflow: hidden; margin-bottom: 14px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`
const RecapCell = styled.div`
  background: ${t.surface}; padding: 12px 14px;
`
const RecapLabel = styled.div`
  font-family: ${t.font}; font-size: 0.62rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase; color: ${t.textFaint};
`
const RecapVal = styled.div`
  font-family: ${t.serif}; font-size: 1.14rem; font-weight: 650;
  letter-spacing: -0.03em; color: ${t.text}; margin-top: 4px;
  font-variant-numeric: tabular-nums;
`
const RecapDiff = styled.div`
  font-family: ${t.font}; font-size: 0.7rem; font-weight: 560; margin-top: 2px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$v > 0 ? t.green : p.$v < 0 ? t.red : t.textFaint)};
`

/* ── TVA française : suivi interne ── */
const TvaRow = styled.div`
  display: flex; align-items: center; gap: 9px; flex-wrap: wrap;
  margin-top: 10px; padding-top: 9px;
  border-top: 1px dashed ${t.line};
`
const TvaLabel = styled.span`
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 600; color: ${t.textSoft};
  em {
    font-style: normal; font-size: 0.6rem; font-weight: 500; color: ${t.textFaint};
    margin-left: 5px; text-transform: uppercase; letter-spacing: 0.07em;
  }
`
const TvaModes = styled.div`display: flex; gap: 3px;`
const TvaMode = styled.button`
  font-family: ${t.font}; font-size: 0.65rem; font-weight: 600;
  padding: 3px 9px; border-radius: ${t.rXs}; cursor: pointer;
  white-space: nowrap; min-width: 26px;
  background: ${(p) => (p.$on ? t.blue : t.surface)};
  color: ${(p) => (p.$on ? '#fff' : t.textFaint)};
  border: 1px solid ${(p) => (p.$on ? t.blue : t.line)};
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  &:hover { border-color: ${t.blue}; color: ${(p) => (p.$on ? '#fff' : t.blue)}; }
`
const TvaMontant = styled.span`
  margin-left: auto;
  font-family: ${t.font}; font-size: 0.75rem; font-weight: 620;
  color: ${t.blue}; font-variant-numeric: tabular-nums;
`
const TvaTotal = styled.div`
  display: flex; align-items: baseline; gap: 9px; flex-wrap: wrap;
  background: ${t.blueWash};
  border: 1px dashed #C3D9EE;
  border-radius: ${t.rSm};
  padding: 9px 13px; margin-bottom: 14px;
  font-family: ${t.font};
  span { font-size: 0.66rem; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: ${t.blue}; opacity: 0.8; }
  b { font-size: 0.88rem; font-weight: 650; color: ${t.blue}; font-variant-numeric: tabular-nums; }
  em { font-style: normal; font-size: 0.65rem; color: ${t.textFaint}; margin-left: auto; }
`

const EcartCompta = styled.div`
  display: flex; align-items: baseline; gap: 7px;
  font-family: ${t.font}; font-size: 0.73rem; color: ${t.textSoft};
  margin: -6px 0 14px;
  b {
    font-size: 0.85rem; font-weight: 650; font-variant-numeric: tabular-nums;
    color: ${(p) => (p.$v > 0 ? t.green : t.red)};
  }
`

const nb = (v) => (Number.isFinite(v) ? v : 0)

export default function CarnetLivraison({ onValidationAlert }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useUI()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [lignes, setLignes] = useState([])
  const [caCompta, setCaCompta] = useState('')
  const [caReel, setCaReel] = useState('')
  const [camions, setCamions] = useState('')
  const [dateLiv, setDateLiv] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('orders').select('*, clients(name)').eq('id', id).maybeSingle()
    if (!data) { setNotFound(true); setLoading(false); return }
    setOrder(data)
    setLignes((data.lignes || []).map((l) => ({
      produit: l.produit,
      poids_t: l.poids_t ?? '',
      prix_100kg: l.prix_100kg ?? '',
      taxe_100kg: l.taxe_100kg ?? '0',
      tva_pct: l.tva_pct ?? '',
      nb_camions: l.nb_camions ?? '',
      _prevu: { poids: nb(l.poids_t), prix: nb(l.prix_100kg), camions: nb(l.nb_camions), total: nb(l.sous_total) },
    })))
    setCaCompta(data.ca_compta_chf != null ? String(data.ca_compta_chf) : '')
    setCaReel(data.ca_reel_chf != null ? String(data.ca_reel_chf) : '')
    setCamions(data.nb_camions_total != null ? String(data.nb_camions_total) : '')
    setDateLiv(data.date_livraison || new Date().toISOString().split('T')[0])
    setLoading(false)
  }, [id])

  useEffect(() => { load() }, [load])

  const maj = (i, champ, val) =>
    setLignes((prev) => prev.map((l, idx) => (idx === i ? { ...l, [champ]: val } : l)))

  const dejaLivree = order?.status === 'delivered'
  const totaux = totauxDe(lignes)
  const prevuCA = nb(order?.ca_total_chf)
  const prevuPoids = nb(order?.poids_total_t)
  const ecartCA = totaux.ca_total_chf - prevuCA
  const ecartPoids = totaux.poids_total_t - prevuPoids

  const confirmer = async () => {
    setSaving(true)
    // Vide = on reprend le montant calculé depuis les lignes
    const compta = caCompta !== '' ? parseFloat(caCompta) : totaux.ca_total_chf
    const percu = caReel !== '' ? parseFloat(caReel) : compta

    const ok = await ecrire(
      supabase.from('orders').update({
        lignes: totaux.lignes,
        poids_total_t: totaux.poids_total_t,
        ca_total_chf: totaux.ca_total_chf,
        ca_compta_chf: compta,
        ca_reel_chf: percu,
        nb_camions_total: camions === '' ? null : parseInt(camions, 10),
        date_livraison: dateLiv || null,
        status: 'delivered',
      }).eq('id', id),
      toast,
      'enregistrement de la livraison',
    )
    // Rien n'a été écrit : on reste sur la page pour ne rien perdre
    if (!ok) { setSaving(false); return }

    // Une livraison effectuée fait passer le client en fidèle
    if (order.client_id) {
      await ecrire(
        supabase.from('clients').update({ type: 'fidele' }).eq('id', order.client_id),
        toast, 'mise à jour du client',
      )
    }
    onValidationAlert?.()
    setSaving(false)
    toast(dejaLivree ? 'Corrections enregistrées' : 'Livraison enregistrée')
    navigate(`/admin/carnet/${id}`)
  }

  if (loading) {
    return (
      <>
        <BackLink onClick={() => navigate(`/admin/carnet/${id}`)}>← Retour à la commande</BackLink>
        <Loading rows={3} h="120px" />
      </>
    )
  }
  if (notFound) {
    return (
      <>
        <BackLink onClick={() => navigate('/admin/carnet')}>← Retour au carnet</BackLink>
        <Empty icon="🔍" title="Commande introuvable" />
      </>
    )
  }

  return (
    <>
      <BackLink onClick={() => navigate(`/admin/carnet/${id}`)}>← Retour à la commande</BackLink>

      <Intro>
        <IntroTitle>{dejaLivree ? '✏️ Modifier la clôture' : '📦 Clôturer la livraison'}</IntroTitle>
        <Muted>
          {order.clients?.name} — {dejaLivree
            ? "cette livraison est déjà enregistrée. Vous pouvez corriger les montants, notamment renseigner le montant compta s'il manquait."
            : 'ajustez les quantités réellement livrées et facturées. Ce que vous saisissez ici remplace les valeurs prévisionnelles de la commande.'}
        </Muted>
      </Intro>

      <Row $cols="1.15fr 0.85fr" $gap="16px">
        {/* Lignes réelles */}
        <Stagger $i={0}>
          <Panel>
            <PanelHead><PanelTitle>Quantités livrées</PanelTitle></PanelHead>

            {lignes.map((l, i) => {
              const douane = l.produit === 'Frais de douane'
              const c = calcLigne(l)
              const dPoids = c.p !== l._prevu.poids
              const dPrix = c.pr !== l._prevu.prix
              return (
                <LigneBox key={i}>
                  <LigneName>{l.produit}</LigneName>
                  {douane ? (
                    <Field $flush>
                      <Label>Montant réel</Label>
                      <Input type="number" value={l.prix_100kg} onChange={(e) => maj(i, 'prix_100kg', e.target.value)} />
                      <Prevu $diff={dPrix}>prévu : <b>{montant(l._prevu.prix)}</b></Prevu>
                    </Field>
                  ) : (
                    <Row $cols="1fr 1fr 1fr" $gap="9px">
                      <Field $flush>
                        <Label>Poids exact (T)</Label>
                        <Input type="number" step="0.1" value={l.poids_t} onChange={(e) => maj(i, 'poids_t', e.target.value)} />
                        <Prevu $diff={dPoids}>prévu : <b>{l._prevu.poids} T</b></Prevu>
                      </Field>
                      <Field $flush>
                        <Label>Prix exact /100kg</Label>
                        <Input type="number" step="0.01" value={l.prix_100kg} onChange={(e) => maj(i, 'prix_100kg', e.target.value)} />
                        <Prevu $diff={dPrix}>prévu : <b>{l._prevu.prix.toFixed(2)}</b></Prevu>
                      </Field>
                      <Field $flush>
                        <Label>Taxe /100kg</Label>
                        <Input type="number" step="0.01" value={l.taxe_100kg} onChange={(e) => maj(i, 'taxe_100kg', e.target.value)} />
                      </Field>
                    </Row>
                  )}
                  <TvaRow>
                    <TvaLabel>TVA française <em>hors total</em></TvaLabel>
                    <TvaModes>
                      <TvaMode $on={!l.tva_pct} onClick={() => maj(i, 'tva_pct', '')}>—</TvaMode>
                      {TAUX_TVA.map((taux) => (
                        <TvaMode key={taux} $on={parseFloat(l.tva_pct) === taux}
                          onClick={() => maj(i, 'tva_pct', taux)}>
                          {String(taux).replace('.', ',')} %
                        </TvaMode>
                      ))}
                    </TvaModes>
                    {l.tva_pct ? <TvaMontant>{montant(tvaDe({ ...l, prix_100kg: l.prix_100kg, poids_t: l.poids_t }).montant)}</TvaMontant> : null}
                  </TvaRow>

                  <SousTotal>Sous-total <b>{montant(c.total)}</b></SousTotal>
                </LigneBox>
              )
            })}
          </Panel>
        </Stagger>

        {/* Clôture */}
        <Stagger $i={1}>
          <Panel $accent>
            <PanelHead><PanelTitle>Récapitulatif</PanelTitle></PanelHead>

            <Recap>
              <RecapCell>
                <RecapLabel>Tonnage</RecapLabel>
                <RecapVal>{totaux.poids_total_t.toFixed(1)} T</RecapVal>
                {ecartPoids !== 0 && (
                  <RecapDiff $v={ecartPoids}>{ecartPoids > 0 ? '+' : ''}{ecartPoids.toFixed(1)} T</RecapDiff>
                )}
              </RecapCell>
              <RecapCell>
                <RecapLabel>Camions</RecapLabel>
                <RecapVal>{camions === '' ? totaux.nb_camions : camions}</RecapVal>
              </RecapCell>
              <RecapCell>
                <RecapLabel>Montant</RecapLabel>
                <RecapVal>{montant(totaux.ca_total_chf)}</RecapVal>
                {ecartCA !== 0 && (
                  <RecapDiff $v={ecartCA}>{montant(ecartCA, { signe: true })}</RecapDiff>
                )}
              </RecapCell>
            </Recap>

            {tvaTotaleDe(lignes) > 0 && (
              <TvaTotal>
                <span>TVA française</span>
                <b>{montant(tvaTotaleDe(lignes))}</b>
                <em>suivi interne, hors montant facturé</em>
              </TvaTotal>
            )}

            <Field>
              <Label>Camions réellement utilisés</Label>
              <Input
                type="number" min="0" value={camions}
                onChange={(e) => setCamions(e.target.value)}
                placeholder={String(totaux.nb_camions)}
              />
              <Muted style={{ fontSize: '0.74rem', marginTop: 6 }}>
                Vide = calcul automatique sur le poids total ({totaux.poids_total_t.toFixed(1)} T ÷ {CAPACITE_CAMION}
                = {totaux.nb_camions}). Un camion transporte plusieurs marchandises.
              </Muted>
            </Field>

            <Field>
              <Label>Date de livraison</Label>
              <Input type="date" value={dateLiv} onChange={(e) => setDateLiv(e.target.value)} />
            </Field>

            <Row $cols="1fr 1fr" $gap="10px">
              <Field>
                <Label>Montant compta</Label>
                <Input type="number" value={caCompta}
                  onChange={(e) => setCaCompta(e.target.value)}
                  placeholder={totaux.ca_total_chf.toFixed(0)} />
                <Muted style={{ fontSize: '0.72rem', marginTop: 5 }}>
                  Ce qui est facturé et déclaré. Vide = montant calculé ci-dessus.
                </Muted>
              </Field>
              <Field>
                <Label>Réel perçu</Label>
                <Input type="number" value={caReel}
                  onChange={(e) => setCaReel(e.target.value)}
                  placeholder={(caCompta !== '' ? parseFloat(caCompta) : totaux.ca_total_chf).toFixed(0)} />
                <Muted style={{ fontSize: '0.72rem', marginTop: 5 }}>
                  Ce que tu as réellement gagné. Vide = identique au montant compta.
                </Muted>
              </Field>
            </Row>

            {caReel !== '' && caCompta !== '' && parseFloat(caReel) !== parseFloat(caCompta) && (
              <EcartCompta $v={parseFloat(caReel) - parseFloat(caCompta)}>
                Écart réel / compta&nbsp;:
                <b>{montant(parseFloat(caReel) - parseFloat(caCompta), { signe: true })}</b>
              </EcartCompta>
            )}

            <Cluster $gap="9px">
              <Btn $variant="gold" onClick={confirmer} disabled={saving} style={{ flex: 1 }}>
                {saving ? <><Spinner /> Enregistrement…</> : dejaLivree ? '✓ Enregistrer les corrections' : '✓ Confirmer la livraison'}
              </Btn>
              <Btn $variant="ghost" onClick={() => navigate(`/admin/carnet/${id}`)} disabled={saving}>
                Annuler
              </Btn>
            </Cluster>

            <Muted style={{ fontSize: '0.74rem', marginTop: 11 }}>
              {dejaLivree
                ? 'La commande reste en « Livré ». Seuls les montants et quantités sont mis à jour.'
                : 'La commande passera en « Livré » et le client en « fidèle ».'}
            </Muted>
          </Panel>
        </Stagger>
      </Row>
    </>
  )
}
